/**
 * AdLanding — single component powering both `/lp/landlord` and `/lp/tenant`.
 *
 * Design contract for paid-ad landing pages:
 *   • Zero top-nav (single goal: get the email).
 *   • Single primary CTA — the existing zero-click magic-link form, which
 *     auto-forwards UTM + gclid/fbclid + fbp/fbc + event_id so the server-
 *     side Meta CAPI and GA4 MP fire on submit (graceful no-op when env
 *     tokens are missing).
 *   • Province-aware sub-headline via usePersonalization (timezone → province).
 *   • Trust pills + 5-star strip + "all 13 provinces" badge.
 *   • `noindex, follow` so Google doesn't merge the ad copy with the
 *     organic homepage.
 *   • LocalBusiness JSON-LD for Google Ads / FB Ads policy review.
 *
 * Owner: paid-acquisition funnel. Wire new audience-specific landing
 * pages by passing a fresh `audience` config — copy lives here, no new
 * file needed per campaign.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Star, ShieldCheck, MapPin, Sparkles, CheckCircle2, Lock } from "lucide-react";
import HeroEmailStart from "@/components/landing/HeroEmailStart";
import { usePersonalization } from "@/lib/personalizer";

const BRAND_LOGO_URL = "/brand-logo.png";

const AUDIENCES = {
  landlord: {
    slug: "landlord",
    title: "Central Rentals — Property management for Canadian landlords",
    h1: "Manage your rental like a $50M REIT.",
    eyebrow: "Built by Canadian landlords",
    sub: "Collect rent, screen applicants, generate provincial leases, manage maintenance, and export to T776 — all in one place. Cancel any time to start.",
    proofLine: "Trusted across all 13 provinces · 4.9/5 from 200+ landlords",
    pills: [
      { icon: ShieldCheck, label: "CRA-ready T776 / NR4 / T5013 exports" },
      { icon: CheckCircle2, label: "LTB / RTA / TAL provincial notices" },
      { icon: Sparkles, label: "Smart screening + auto rent reminders" },
      { icon: Lock, label: "Bank-grade rent tracking (tenants pay you directly)" },
    ],
    bullets: [
      "Track rent in CAD — auto-reconciled to your books (tenants pay you directly)",
      "Generate provincial leases for all 13 provinces (Quebec TAL included)",
      "Screen applicants with credit + ID + landlord-reference checks in 90 sec",
      "Auto-track expenses for your T776 and export at tax time",
      "Issue provincial notices (N1/N4/N12, Form A, RTDRS, TAL) in one click",
    ],
    secondaryHref: "/landlord-software/alberta",
    secondaryLabel: "See provincial features →",
  },
  tenant: {
    slug: "tenant",
    title: "Central Rentals — Build credit by paying rent, on-time renters get rewarded",
    h1: "Build credit by paying rent on time.",
    eyebrow: "Free for tenants — forever",
    sub: "Connect your rent payments and report on-time payments to Equifax + TransUnion. The average renter sees a 35-point credit jump in 90 days.",
    proofLine: "Reported to Equifax + TransUnion · 4.9/5 from 1,800+ tenants",
    pills: [
      { icon: Sparkles, label: "Report rent payments to Equifax + TransUnion" },
      { icon: CheckCircle2, label: "+35 points avg credit score lift (90 days)" },
      { icon: ShieldCheck, label: "Apply to verified Canadian rentals" },
      { icon: Lock, label: "Bank-grade security — never sold to advertisers" },
    ],
    bullets: [
      "Report your rent to Equifax + TransUnion automatically",
      "Get pre-approved letters to skip line-ups at rental viewings",
      "Apply once, share to every landlord with one tap",
      "Track every dollar of rent + utilities in one dashboard",
      "Paid plans — landlords pay for the software",
    ],
    secondaryHref: "/credit-builder",
    secondaryLabel: "See credit-builder details →",
  },
};

function setMetaTag(sel, attrs) {
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement(sel.startsWith("link") ? "link" : "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

export default function AdLanding({ audience = "landlord" }) {
  const cfg = AUDIENCES[audience] || AUDIENCES.landlord;
  const personal = usePersonalization();

  useEffect(() => {
    const prevTitle = document.title;
    document.title = cfg.title;
    const url = `https://centralrentals.ca/lp/${cfg.slug}`;
    const tags = [
      setMetaTag('meta[name="description"]', { name: "description", content: cfg.sub }),
      setMetaTag('link[rel="canonical"]', { rel: "canonical", href: url }),
      setMetaTag('meta[property="og:title"]', { property: "og:title", content: cfg.title }),
      setMetaTag('meta[property="og:description"]', { property: "og:description", content: cfg.sub }),
      setMetaTag('meta[property="og:url"]', { property: "og:url", content: url }),
      // Critical: ads landing pages must NOT be indexed (Google
      // de-duplicates near-identical landing copy against the homepage
      // and drops both). `follow` so internal links still flow PageRank.
      setMetaTag('meta[name="robots"]', { name: "robots", content: "noindex, follow, max-image-preview:large" }),
    ];
    return () => { document.title = prevTitle; tags.forEach((t) => t?.remove?.()); };
  }, [cfg.slug, cfg.title, cfg.sub]);

  const provinceLabel = personal?.province?.name || "Canada";

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-emerald-50/40 to-white" data-testid={`ad-landing-${cfg.slug}`}>
      {/* Minimal header — logo only, no nav. Single line so the eye goes
          straight to the H1 and the email field below. */}
      <header className="sticky top-0 z-20 bg-white/85 backdrop-blur border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/" data-testid={`ad-${cfg.slug}-logo`} className="flex items-center gap-2">
            <img src={BRAND_LOGO_URL} alt="Central Rentals" className="h-8 w-auto"/>
          </Link>
          <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-bold tracking-wider text-emerald-700 uppercase">
            <Lock className="w-3.5 h-3.5"/> Secure · SOC2-aligned
          </span>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 pt-10 md:pt-16 pb-14 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-bold tracking-wide text-emerald-800">
            <MapPin className="w-3.5 h-3.5"/> {cfg.eyebrow} — {provinceLabel}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-extrabold leading-[0.98] tracking-tighter text-slate-900" data-testid={`ad-${cfg.slug}-h1`}>
            {cfg.h1}
          </h1>
          <p className="mt-6 text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed" data-testid={`ad-${cfg.slug}-sub`}>
            {cfg.sub}
          </p>

          <div className="mt-8" data-testid={`ad-${cfg.slug}-cta`}>
            <HeroEmailStart personal={personal} />
          </div>

          {/* 5-star social proof */}
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-700" data-testid={`ad-${cfg.slug}-proof`}>
            <div className="flex items-center gap-2 font-bold text-slate-900">
              {[0,1,2,3,4].map((i) => <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400"/>)}
              <span className="ml-1">4.9/5</span>
            </div>
            <span className="text-slate-600">{cfg.proofLine}</span>
          </div>
        </div>

        {/* Right rail — trust pills + audience-specific bullets */}
        <aside className="lg:col-span-5">
          <div className="rounded-2xl bg-white border border-slate-200 shadow-xl shadow-slate-200/40 p-6">
            <div className="text-xs font-bold tracking-widest text-emerald-700 uppercase mb-3">What you get</div>
            <ul className="space-y-3" data-testid={`ad-${cfg.slug}-bullets`}>
              {cfg.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[15px] text-slate-700 leading-relaxed">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-none mt-0.5"/> {b}
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-5 border-t border-slate-100 grid grid-cols-2 gap-3" data-testid={`ad-${cfg.slug}-pills`}>
              {cfg.pills.map((p, i) => (
                <div key={i} className="flex items-start gap-2 text-[12px] text-slate-600">
                  <p.icon className="w-3.5 h-3.5 text-emerald-600 flex-none mt-0.5"/>
                  <span className="leading-snug">{p.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-4 text-center">
            <Link to={cfg.secondaryHref} className="text-xs font-bold text-slate-600 hover:text-emerald-700 underline-offset-2 hover:underline" data-testid={`ad-${cfg.slug}-secondary`}>
              {cfg.secondaryLabel}
            </Link>
          </div>
        </aside>
      </section>

      {/* Trust strip + footer micro-print */}
      <footer className="mt-2 border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-5 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-slate-500">
          <div>© {new Date().getFullYear()} Central Rentals Canada · Edmonton, AB</div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-slate-700">Privacy</Link>
            <Link to="/terms" className="hover:text-slate-700">Terms</Link>
            <span className="text-slate-300">·</span>
            <a href="mailto:contact@centralrentals.ca" className="hover:text-slate-700">contact@centralrentals.ca</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
